import FallbackImage from "./ImageWrapper";
import {
  buildThemeCssVariablesFromColors,
  normalizeHexColor,
  DEFAULT_THEME,
} from "../../theme/companyTheme";

/**
 * Scoped portal mockup — cooperative CSS variables apply only inside this component.
 * Does not affect the internal admin app shell.
 */
export default function CooperativeThemePreview({
  primaryColor,
  secondaryColor,
  theme,
  logoUrl,
  companyName = "Organization Name",
}) {
  const primary = normalizeHexColor(
    primaryColor ?? theme?.primary ?? theme?.primary_color,
    DEFAULT_THEME.primary
  );
  const secondary = normalizeHexColor(
    secondaryColor ?? theme?.secondary ?? theme?.secondary_color,
    DEFAULT_THEME.secondary
  );

  const cssVars = buildThemeCssVariablesFromColors(primary, secondary);
  const primaryHover = `color-mix(in srgb, ${primary} 80%, black)`;

  const inactiveMenuBg = "transparent";
  const inactiveMenuColor = "#525252";

  return (
    <div
      className="rounded-3 p-3"
      style={{ background: "#f7f7f7", border: "1px solid #e0e0e0" }}
    >
      <p className="fw-semibold mb-1" style={{ color: "#383838" }}>
        Portal preview
      </p>
      <p className="small text-muted mb-3">
        Draft colors for this cooperative — saved only when you submit the form.
        The preview mirrors how the member/staff portal will render these colors.
      </p>
      <div
        className="rounded-3 overflow-hidden shadow-sm"
        style={{
          ...cssVars,
          background: "#fff",
          border: "1px solid var(--color-border)",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2"
          style={{
            borderBottom: "1px solid var(--color-border)",
            background: "#fff",
            minHeight: "44px",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "6px",
                overflow: "hidden",
                border: "1px solid var(--color-border)",
                flexShrink: 0,
              }}
            >
              <FallbackImage
                src={logoUrl}
                alt=""
                width="28"
                height="28"
                objectFit="cover"
                fallback="/Images/logo1.svg"
              />
            </div>
            <span className="fw-semibold" style={{ color: "#383838", fontSize: "13px" }}>
              {companyName}
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span
              title="Secondary accent dot (exact brand color)"
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "var(--color-secondary)",
              }}
            />
            <span
              style={{
                width: "26px",
                height: "26px",
                borderRadius: "50%",
                background: "var(--color-primary-soft)",
              }}
            />
          </div>
        </div>

        <div className="d-flex" style={{ minHeight: "240px" }}>
          <div
            style={{
              width: "88px",
              background: "#fff",
              borderRight: "1px solid var(--color-border)",
              padding: "10px 8px",
            }}
          >
            <div
              style={{
                height: "6px",
                borderRadius: "3px",
                background: "#e0e0e0",
                marginBottom: "10px",
                opacity: 0.6,
              }}
            />
            <div
              style={{
                background: "var(--color-primary)",
                color: "var(--color-primary-text)",
                borderRadius: "6px",
                padding: "6px 8px",
                fontSize: "11px",
                fontWeight: 600,
                marginBottom: "6px",
              }}
            >
              Dashboard
            </div>
            <div
              style={{
                background: inactiveMenuBg,
                color: inactiveMenuColor,
                borderRadius: "6px",
                padding: "6px 8px",
                fontSize: "11px",
                marginBottom: "6px",
              }}
            >
              Members
            </div>
            <div
              style={{
                background: "var(--color-primary-soft)",
                color: "var(--color-primary-readable)",
                borderRadius: "6px",
                padding: "6px 8px",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              Loans
            </div>
          </div>

          <div className="flex-grow-1 p-3" style={{ background: "#fafafa" }}>
            <div
              className="rounded p-3 mb-3"
              style={{
                border: "1px solid var(--color-border)",
                background: "#fff",
              }}
            >
              <div className="fw-semibold mb-2" style={{ color: "#45464e", fontSize: "13px" }}>
                Summary card
              </div>
              <a
                href="#preview"
                onClick={(e) => e.preventDefault()}
                style={{
                  color: "var(--color-secondary-readable)",
                  fontSize: "13px",
                  textDecoration: "underline",
                }}
              >
                View details link
              </a>
            </div>

            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              <button
                type="button"
                style={{
                  background: "var(--color-primary)",
                  color: "var(--color-primary-text)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "7px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                Primary action
              </button>
              <span
                style={{
                  background: "var(--color-secondary)",
                  color: "var(--color-secondary-text)",
                  borderRadius: "999px",
                  padding: "4px 10px",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                Secondary badge
              </span>
            </div>

            <div
              className="rounded overflow-hidden"
              style={{ border: "1px solid #e0e0e0", fontSize: "12px" }}
            >
              <div
                style={{
                  background: "var(--color-primary-dark)",
                  color: "#fff",
                  padding: "6px 10px",
                  fontWeight: 600,
                }}
              >
                Recent activity
              </div>
              <div
                className="d-flex align-items-center justify-content-between px-2 py-2"
                style={{ background: "#fff", borderBottom: "1px solid #e0e0e0" }}
              >
                <span style={{ color: "#45464e" }}>Contribution payment</span>
                <button
                  type="button"
                  style={{
                    background: "transparent",
                    color: "var(--color-primary-readable)",
                    border: "1px solid var(--color-primary)",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  View
                </button>
              </div>
              <div
                className="d-flex align-items-center justify-content-between px-2 py-2"
                style={{ background: "#fff" }}
              >
                <span style={{ color: "#45464e" }}>Loan repayment</span>
                <span style={{ color: "var(--color-secondary-readable)", fontWeight: 600 }}>
                  Paid
                </span>
              </div>
            </div>

            <div className="mt-2 small text-muted" style={{ fontSize: "10px" }}>
              Hover state sample:{" "}
              <span style={{ color: primaryHover, fontWeight: 600 }}>
                darker primary
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
